import { Alert, flash, Modal, Select, SubmitButton, Table } from "@xola/ui-kit";
import clsx from "clsx";
import {
    FormField,
    getPartnerFee,
    getPaymentLineItems,
    handleSuccessfulSubmission,
    InputField,
    isMembershipItem,
    isSettledByCancellationProtection,
    requiredString,
} from "common";
import { useFormikContext } from "formik";
import { isUndefined, sumBy } from "lodash";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    Card,
    LineItemObject,
    LineItemType,
    PaymentMethod,
    PaymentMethodCopy,
    PaymentMethodType,
    Purchase,
    PurchaseItem,
    Seller,
    SellerPaymentMethod,
    Traveler,
} from "types";
import { array, boolean, lazy, number, object, ref } from "yup";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { isOnFlexSubscription } from "../../models/PartnerFeeFormula";
import { getBackofficePaymentMethods, getOnlinePaymentMethodsFromPurchase } from "../../models/Payment";
import {
    getInteracPaymentLineItem,
    getLineItemById,
    getPaymentFormItems,
    getPaymentLineItemsWithoutRefunds,
    getPaymentLineItemsWithRefunds,
    isExperiencePurchaseItem,
    shouldEnableDrawerEject,
} from "../../models/Purchase";
import { getAutoPrint } from "../../storages/PrinterStorage";
import { log } from "../../utils/log";
import { Currency } from "../Common/Currency";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { Printer } from "../Common/Printer";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { RefundMethod } from "../Event/CancelBookings/EventBookingsTable";
import { CurrencyInput } from "../Input/CurrencyInput";
import { getPairedDevice } from "../Input/EmvInput";
import { EmvRefundInput } from "../Input/EmvRefundInput";
import { PaymentInputRef } from "../Input/PaymentInputRef";
import { Loader } from "../Loader";
import { CancellationProtectionRefundBlocked } from "./Refund/CancellationProtectionRefundBlocked";
import { InteracPaymentMessage } from "./SavedCardMenu";
import { queryClient } from "@/api/queryClient";
import { isAuthorizeNetEnabled } from "@/models/Seller";

interface SplitPayRefundModalProps {
    isOpen: boolean;
    purchase: Purchase;
    item: PurchaseItem;
    shouldReduce: boolean;
    onClose: () => void;
    filterTravelerId?: ID;
}

export const SplitPayRefundModal = ({
    isOpen,
    purchase,
    item,
    shouldReduce = true,
    onClose,
    filterTravelerId,
}: SplitPayRefundModalProps) => {
    if (isSettledByCancellationProtection(purchase, item)) {
        return (
            <Modal className="min-w-[1000px]" isOpen={isOpen} onClose={onClose}>
                <CancellationProtectionRefundBlocked onClose={onClose} />
            </Modal>
        );
    }

    return (
        <Modal className="min-w-[1000px]" isOpen={isOpen} onClose={onClose}>
            <ModalContent
                purchase={purchase}
                item={item}
                shouldReduce={shouldReduce}
                filterTravelerId={filterTravelerId}
                onClose={onClose}
            />
        </Modal>
    );
};

const ModalContent = ({
    purchase,
    item,
    shouldReduce,
    onClose,
    filterTravelerId,
}: Except<SplitPayRefundModalProps, "isOpen">) => {
    const seller = useSeller();
    const isRemovePayerMode = !!filterTravelerId;
    const invalidate = useInvalidate();
    const [notify, setNotify] = useState(true);
    const [enableDrawerEject, setEnableDrawerEject] = useState<boolean>(false);
    const hasDevice = !!getPairedDevice(seller) || window.flags?.stripe?.simulateTerminal;
    const emvRef = useRef<PaymentInputRef>(null);

    const {
        mutateAsync: returnPayment,
        isLoading: isUpdating,
        isSuccess: isUpdatingSuccess,
    } = useMutation(async (payload: ReturnType<typeof getPayload>) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
        return data;
    });

    const {
        mutateAsync: storeCreditPayment,
        isLoading: isStoreCrediting,
        isSuccess: isStoreCreditingSuccess,
    } = useMutation(async (payload: ReturnType<typeof getStoreCreditPayload>) => {
        return api.post(`/purchases/${purchase.id}/lineItems`, payload);
    });

    const handleSubmit = async (values: FormValues) => {
        try {
            const refundAmount: number[] = [0]; // Keep refunded amounts to show it later in flash message

            // adding a separate variable to ensure the updated purchase goes to print.
            let updatedPurchase: Purchase;
            for (const [, paymentFormItems] of Object.entries(values.paymentFormItems)) {
                for (const paymentFormItem of paymentFormItems) {
                    refundAmount.push(paymentFormItem.refundAmount);
                    const isInteracCardRefundFlow =
                        paymentFormItem.paymentMethodType === PaymentMethodType.INTERAC &&
                        paymentFormItem.refundMethod === PaymentMethod.CREDIT_CARD;
                    const isValidRefund =
                        paymentFormItem.refundTerm !== RefundTerms.NONE && paymentFormItem.refundAmount !== 0; // skip if refund amount is 0;

                    if (isInteracCardRefundFlow && isValidRefund) {
                        const paymentLineItem = getLineItemById(purchase, paymentFormItem.id);
                        const params = {
                            amountDue: paymentFormItem.refundAmount,
                            last4: paymentLineItem?.payment?.meta?.cardSummary?.last4,
                            clientSecret: getInteracPaymentLineItem(
                                purchase,
                                paymentLineItem?.payment?.meta?.cardSummary,
                            )?.payment?.remoteId,
                        };

                        if (shouldReduce) {
                            const payload = getPayload(paymentFormItem, item, values.reason, notify, shouldReduce);
                            payload.ops.pop(); // Remove the refund line item from ops, as EMV will handle it separately
                            updatedPurchase = await returnPayment(payload);
                        }

                        try {
                            await emvRef.current?.confirm?.(params);
                        } catch {}

                        updatedPurchase = queryClient.getQueryData<Purchase>(["purchases", purchase.id]) ?? purchase;
                    }

                    if (paymentFormItem.refundMethod === PaymentMethod.STORE_CREDIT) {
                        await storeCreditPayment(getStoreCreditPayload(paymentFormItem, values.reason, notify));
                    } else if (!isInteracCardRefundFlow && isValidRefund) {
                        updatedPurchase = await returnPayment(
                            getPayload(paymentFormItem, item, values.reason, notify, shouldReduce),
                        );
                    }
                }
            }

            handleSuccessfulSubmission(() => {
                if (getAutoPrint() && updatedPurchase) {
                    const drawerEjectPreference = shouldEnableDrawerEject(updatedPurchase);
                    setEnableDrawerEject(drawerEjectPreference);
                }

                invalidate("purchases", purchase.id);
                onClose();
            });
        } catch {
            flash.danger("Error returning payment");
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={getInitialValues(purchase, seller, filterTravelerId)}
            onSubmit={async (values: FormValues) => handleSubmit(values)}
        >
            {({ handleSubmit, values }) => {
                const isInteracCardPresent = hasAnyInteracRefund(values);

                return (
                    <>
                        <Modal.Header>{isRemovePayerMode ? "Remove From Booking" : "Return Payment"}</Modal.Header>

                        <Modal.Body>
                            {isRemovePayerMode ? null : <MaxRefundAmountAlert seller={seller} purchase={purchase} />}
                            <SplitPayRefundTable purchase={purchase} />
                            <Printer
                                enablePaperCut
                                enableDrawerEject={enableDrawerEject}
                                shouldStart={enableDrawerEject}
                            />
                            {isInteracCardPresent && (
                                <InteracPaymentMessage
                                    isEMVConnected={hasDevice}
                                    message="This purchase has at least one payment that was made with an Interac card. For Interac refunds, the customer must present the physical card."
                                />
                            )}
                        </Modal.Body>

                        <Modal.Footer className="flex justify-end">
                            <NotifyCustomerCheckbox
                                isChecked={notify}
                                onChange={(e: any) => setNotify(e.target.checked)}
                            />
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                            <SubmitButton
                                type="submit"
                                isSuccess={isUpdatingSuccess || isStoreCreditingSuccess}
                                isLoading={isUpdating || isStoreCrediting}
                                onClick={handleSubmit}
                            >
                                {isRemovePayerMode ? "Remove" : "Refund"}
                            </SubmitButton>
                        </Modal.Footer>

                        {isInteracCardPresent && <EmvRefundInput ref={emvRef} initialPurchase={purchase} />}
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface SplitPayRefundTableProps {
    purchase: Purchase;
}

const SplitPayRefundTable = ({ purchase }: SplitPayRefundTableProps) => {
    const { values, setFieldValue, errors } = useFormikContext<FormValues>();

    const handleRefundTermChange = (paymentFormItem: PaymentFormItem, refundTerm: RefundTerms, index: number) => {
        const { customerId } = paymentFormItem;
        // Add value for a particular index in the paymentFormItems array, like paymentFormItems.0.refundTerm
        const prefix = `paymentFormItems.[${customerId}].[${index}]`;
        setFieldValue(`${prefix}.refundTerm`, refundTerm);
        switch (refundTerm) {
            case RefundTerms.FULL: {
                setFieldValue(`${prefix}.refundAmount`, paymentFormItem.amountPaid);

                break;
            }

            case RefundTerms.NONE: {
                setFieldValue(`${prefix}.refundAmount`, 0);

                break;
            }

            case RefundTerms.PARTIAL: {
                log("Partial Split-Pay Refund", { paymentFormItem });

                break;
            }
            // No default
        }
    };

    const handlePaymentMethodChange = (paymentFormItem: PaymentFormItem, paymentMethod: string) => {
        const { customerId } = paymentFormItem;
        const index = values.paymentFormItems[customerId].findIndex(
            (paymentItr) => paymentItr.id === paymentFormItem.id,
        );
        const prefix = `paymentFormItems.[${customerId}].[${index}]`;
        setFieldValue(`${prefix}.refundMethod`, paymentMethod);
    };

    const handlePaymentAmountChange = (paymentFormItem: PaymentFormItem, amount: number) => {
        const { customerId } = paymentFormItem;
        const index = values.paymentFormItems[customerId].findIndex(
            (paymentItr) => paymentItr.id === paymentFormItem.id,
        );
        const prefix = `paymentFormItems.[${customerId}].[${index}]`;
        setFieldValue(`${prefix}.refundAmount`, amount);
    };

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Name</Table.Header>
                        <Table.Header>Booking Value</Table.Header>
                        <Table.Header>Amount Paid</Table.Header>
                        <Table.Header className="px-0 ">Payment Method</Table.Header>
                        <Table.Header className="whitespace-nowrap px-0">Refund Terms</Table.Header>
                        <Table.Header>Refund Value</Table.Header>
                        <Table.Header className="pl-0">Refund Method</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body>
                    {Object.keys(values.paymentFormItems).map((customerId) => {
                        const paymentFormItems = values.paymentFormItems[customerId];

                        const isGroupPayment = paymentFormItems.length > 1 && customerId !== "Other";
                        const amountPaid = sumBy(paymentFormItems, "amountPaid");

                        return (
                            <React.Fragment key={`${customerId}-${amountPaid}`}>
                                {isGroupPayment ? (
                                    <SplitPayRefundHeaderRow
                                        key={customerId}
                                        bookingValue={purchase.amount} // Booking value
                                        amountPaid={amountPaid}
                                        paymentFormItem={paymentFormItems[0]}
                                    />
                                ) : null}

                                {paymentFormItems?.map((paymentFormItem, index) => (
                                    <SplitPayRefundRow
                                        key={paymentFormItem.id}
                                        error={
                                            errors.paymentFormItems?.[customerId]
                                                ? (errors.paymentFormItems?.[customerId]?.[index] as any)
                                                : undefined
                                        }
                                        paymentFormItem={paymentFormItem}
                                        purchase={purchase}
                                        value={purchase.amount}
                                        isGroupPayment={isGroupPayment}
                                        onRefundTermChange={(payment, refundTerm) =>
                                            handleRefundTermChange(payment, refundTerm, index)
                                        }
                                        onPaymentMethodChange={handlePaymentMethodChange}
                                        onPaymentAmountChange={handlePaymentAmountChange}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}
                </Table.Body>
            </Table>

            <FormField className="pt-4" name="reason" label="Reason">
                <InputField isRequired />
            </FormField>
        </>
    );
};

const SplitPayRefundRow = ({
    error,
    paymentFormItem,
    purchase,
    value,
    isGroupPayment,
    onRefundTermChange,
    onPaymentMethodChange,
    onPaymentAmountChange,
}: {
    error: any;
    paymentFormItem: PaymentFormItem;
    purchase: Purchase;
    value: number;
    isGroupPayment?: boolean;
    onRefundTermChange: (payment: PaymentFormItem, refundTerm: RefundTerms) => void;
    onPaymentMethodChange: (payment: PaymentFormItem, paymentMethod: string) => void;
    onPaymentAmountChange: (payment: PaymentFormItem, amount: number) => void;
}) => {
    const { data: traveler, isLoading: isFetchingTraveler } = useResource<Traveler>(
        ["users", paymentFormItem.customerId],
        { private: true },
        { enabled: (!isUndefined(paymentFormItem.customerId) && paymentFormItem.customerId !== "Other") ?? false },
    );

    const seller = useSeller();

    if (isFetchingTraveler) {
        return (
            <Table.Row>
                <Table.Cell>
                    <Loader />
                </Table.Cell>
            </Table.Row>
        );
    }

    let methods = [
        // Invoice method not shown for refund
        ...getBackofficePaymentMethods(seller).filter((method) => method.id !== PaymentMethod.INVOICE),
        // TODO: Remove this block once Store Credit logic is removed from getBackofficePaymentMethods
        {
            id: PaymentMethod.STORE_CREDIT,
            name: PaymentMethodCopy[PaymentMethod.STORE_CREDIT],
        },
    ];

    // Add online payment methods (CC + BNPL) from purchase if they match the paid method
    const onlinePaymentMethods = getOnlinePaymentMethodsFromPurchase(purchase);
    const paidMethodFromOnline = onlinePaymentMethods.find((m) => m.id === paymentFormItem.paidMethod);
    if (paidMethodFromOnline) {
        // Add the online payment method if not already in the list (deduplication)
        const existingMethodIds = new Set(methods.map((m) => m.id));
        if (!existingMethodIds.has(paidMethodFromOnline.id)) {
            methods = [paidMethodFromOnline, ...methods];
        }
    }

    return traveler ? (
        <Table.Row
            key={paymentFormItem.id}
            className="border-b border-gray-lighter px-0"
            data-testid="split-pay-refund-modal-row"
        >
            <Table.Cell className="no-translate">
                <div>{traveler.name}</div>
                <div className="text-sm">
                    {paymentFormItem?.last4 ? <span>{`(*${paymentFormItem?.last4})`}</span> : ""}
                </div>
            </Table.Cell>
            <Table.Cell data-testid="split-pay-refund-modal-booking-value">
                {isGroupPayment ? null : <Currency>{value}</Currency>}
            </Table.Cell>
            <Table.Cell data-testid="split-pay-refund-modal-amount-paid">
                <Currency>{Math.abs(paymentFormItem.amountPaid)}</Currency>
            </Table.Cell>
            <Table.Cell className="px-0" data-testid="split-pay-refund-modal-paid-method">
                {methods.find((method) => method.id === paymentFormItem.paidMethod)?.name}
            </Table.Cell>
            <Table.Cell className="px-0">
                <SplitPayRefundTerms paymentFormItem={paymentFormItem} onRefundTermChange={onRefundTermChange} />
            </Table.Cell>
            <Table.Cell className="max-w-20">
                {paymentFormItem.refundTerm &&
                [RefundTerms.NONE, RefundTerms.FULL].includes(paymentFormItem.refundTerm) ? (
                    <Currency>{paymentFormItem.refundAmount}</Currency>
                ) : (
                    <CurrencyInput
                        disabled={paymentFormItem.refundTerm === RefundTerms.NONE}
                        value={paymentFormItem.refundAmount}
                        title={error?.refundAmount}
                        className={clsx(error?.refundAmount && "border-danger")}
                        data-testid="split-pay-refund-modal-refund-amount-input"
                        onValueChange={({ floatValue }) => {
                            if (typeof floatValue !== "undefined") {
                                onPaymentAmountChange(paymentFormItem, floatValue);
                            }
                        }}
                    />
                )}
            </Table.Cell>
            <Table.Cell className="pl-0 ">
                {paymentFormItem.refundTerm === RefundTerms.NONE || paymentFormItem.refundTerm === undefined ? (
                    "None"
                ) : (
                    <SplitPayPaymentMethod
                        paymentFormItem={paymentFormItem}
                        methods={methods}
                        onPaymentMethodChange={onPaymentMethodChange}
                    />
                )}
            </Table.Cell>
        </Table.Row>
    ) : null;
};

const SplitPayRefundHeaderRow = ({
    paymentFormItem,
    bookingValue,
    amountPaid,
}: {
    paymentFormItem: PaymentFormItem;
    bookingValue: number;
    amountPaid: number;
}) => {
    const { data: traveler, isLoading: isFetchingTraveler } = useResource<Traveler>(
        ["users", paymentFormItem.customerId],
        { private: true },
    );

    if (isFetchingTraveler) {
        return (
            <Table.Row>
                <Table.Cell>
                    <Loader />
                </Table.Cell>
            </Table.Row>
        );
    }

    return traveler ? (
        <Table.Row key={paymentFormItem.customerId} className="border-b border-gray-lighter px-0">
            <Table.Cell className="no-translate">{traveler.name}</Table.Cell>
            <Table.Cell>
                <Currency>{bookingValue}</Currency>
            </Table.Cell>
            <Table.Cell>
                <Currency>{amountPaid}</Currency>
            </Table.Cell>
            <Table.Cell className="px-0">Multiple</Table.Cell>
        </Table.Row>
    ) : null;
};

export const enum RefundTerms {
    PARTIAL = "partial",
    FULL = "full",
    NONE = "none",
}

const SplitPayRefundTerms = ({
    paymentFormItem,
    onRefundTermChange,
}: {
    paymentFormItem: PaymentFormItem;
    onRefundTermChange: (paymentFormItem: PaymentFormItem, refundTerm: RefundTerms) => void;
}) => {
    const handleChange = (e: any) => {
        onRefundTermChange(paymentFormItem, e.target.value);
    };

    return (
        <FormField name="refundTerm" className="mb-0">
            <Select
                className="fit-content"
                value={paymentFormItem.refundTerm}
                data-testid="split-pay-refund-modal-refund-term-select"
                onChange={handleChange}
            >
                <option value={RefundTerms.PARTIAL}>Partial</option>
                <option value={RefundTerms.FULL}>Full</option>
                <option value={RefundTerms.NONE}>None</option>
            </Select>
        </FormField>
    );
};

const SplitPayPaymentMethod = ({
    paymentFormItem,
    onPaymentMethodChange,
    methods,
}: {
    paymentFormItem: PaymentFormItem;
    onPaymentMethodChange: (paymentFormItem: PaymentFormItem, paymentMethod: string) => void;
    methods: SellerPaymentMethod[];
}) => {
    const handleChange = (e: any) => {
        const method = methods.find((method) => method.name === e.target.value);
        onPaymentMethodChange(paymentFormItem, method?.id ?? "");
    };

    return (
        <FormField name="refundMethod" className="mb-0">
            <Select
                value={methods.find((method) => method.id === paymentFormItem.refundMethod)?.name}
                className="overflow-hidden pr-7"
                data-testid="split-pay-refund-modal-refund-method-select"
                onChange={handleChange}
            >
                {methods.map((method) => (
                    <option key={method.id} id={method.id} value={method.name}>
                        {method.name}
                    </option>
                ))}
            </Select>
        </FormField>
    );
};

interface FormValues {
    reason: string;
    notify: boolean;
    paymentFormItems: { [key: ID]: PaymentFormItem[] };
}

export interface PaymentFormItem {
    id: ID;
    customerName: string;
    customerId: ID;
    amountPaid: number;
    paidMethod: string;
    refundTerm: RefundTerms;
    refundAmount: number;
    refundMethod: string;
    traveler: { id: ID };
    card?: ID | Card;
    last4?: string;
    paymentMethodType?: string;
}

const getInitialValues = (purchase: Purchase, seller: Seller, filterTravelerId?: ID): FormValues => {
    const lineItemsWithoutRefunds = getPaymentLineItemsWithoutRefunds(purchase);
    const lineItemsWithRefunds = getPaymentLineItemsWithRefunds(purchase);

    const allPaymentFormItems: PaymentFormItem[] = getPaymentFormItems(
        lineItemsWithoutRefunds,
        lineItemsWithRefunds,
        isAuthorizeNetEnabled(seller),
    );

    const paymentFormItems = filterTravelerId
        ? allPaymentFormItems.filter((item) => item.customerId === filterTravelerId)
        : allPaymentFormItems;

    // Group payment form items by customer to display in the table with the header row

    const groupedPaymentFormItems: { [key: ID]: PaymentFormItem[] } = {};

    for (const paymentFormItem of paymentFormItems) {
        const { customerId } = paymentFormItem;
        if (!groupedPaymentFormItems[customerId]) {
            groupedPaymentFormItems[customerId] = [];
        }

        groupedPaymentFormItems[customerId].push(paymentFormItem);
    }

    return {
        reason: "",
        notify: true,
        paymentFormItems: groupedPaymentFormItems,
    };
};

const validationSchema = object({
    reason: requiredString(),
    notify: boolean(),
    paymentFormItems: lazy((object_) =>
        object(
            // eslint-disable-next-line unicorn/no-array-reduce
            Object.keys(object_).reduce((shape: any, key: ID) => {
                // Find non-yup-conflicting way to type this
                shape[key] = array().of(
                    object({
                        refundAmount: number().when("refundTerm", {
                            is: (refundTerm: RefundTerms) => refundTerm === RefundTerms.NONE || key === "Other",
                            then: number().notRequired(),
                            otherwise: number()
                                .required("Refund amount is required")
                                .moreThan(0, "Refund amount must be more than 0")
                                .max(ref("amountPaid"), "Refund amount must be less than Amount paid"),
                        }),
                    }),
                );
                return shape;
            }, {}),
        ),
    ),
});

// Refactor: This function should make payload based on all the form data available, so that can be used in a single api call instead of per lineItem

const getPayload = (
    paymentFormItem: PaymentFormItem,
    item: PurchaseItem,
    name: string,
    notify: boolean,
    shouldReduce: boolean,
) => {
    const ops: any[] = [];

    // Add the first object only if item exists
    if (item && shouldReduce) {
        const retainPriceStrategy = isMembershipItem(item) || isExperiencePurchaseItem(item);
        ops.push({
            path: `/items/${item.id}/lineItems`,
            op: "addLineItem",
            data: {
                amount: paymentFormItem.refundAmount * -1,
                name: name || "Reduce booking value",
                object: LineItemObject.DEFAULT,
                type: LineItemType.ADJUSTMENT,
                _emailOverrides: { sendEmailToTraveler: notify },
                traveler: paymentFormItem.traveler,
                ...(retainPriceStrategy ? { _priceStrategy: "retain" } : null),
            },
        });
    }

    // The user only wants to return payment, so we add this below object in this case
    ops.push({
        path: "/lineItems",
        op: "addLineItem",
        data: {
            amount: paymentFormItem.refundAmount,
            name: "Return Payment",
            object: LineItemObject.REFUND,
            payload: LineItemType.REFUND,
            payment: {
                method: paymentFormItem.refundMethod,
                card:
                    paymentFormItem.refundMethod === PaymentMethod.CREDIT_CARD && paymentFormItem.card
                        ? paymentFormItem.card
                        : undefined,
                comment: name,
            },
            _emailOverrides: { sendEmailToTraveler: notify },
            traveler: paymentFormItem.traveler,
        },
    });

    return {
        ops,
    };
};

const getStoreCreditPayload = (paymentFormItem: PaymentFormItem, name: string, notify: boolean) => {
    return {
        name: "Return Payment",
        object: LineItemObject.REFUND,
        amount: paymentFormItem.refundAmount,
        refundToStoreCredit: true,
        payment: { method: paymentFormItem.paidMethod, comment: name },
        payload: "refund",
        _emailOverrides: { sendEmailToTraveler: notify },
        traveler: paymentFormItem.traveler,
    };
};

const MaxRefundAmountAlert = ({ seller, purchase }: { seller: Seller; purchase: Purchase }) => {
    const { values } = useFormikContext<FormValues>();
    const customerIds = Object.keys(values.paymentFormItems);
    const refundMethods = customerIds.flatMap((customerId) =>
        values.paymentFormItems[customerId].map((formItem) => formItem.refundMethod),
    );
    const isStoreCreditOnlyRefund = refundMethods.every((method) => method === RefundMethod.STORE_CREDIT);
    const partnerFeeAmount = getPartnerFee(purchase)?.amount;
    const maxRefundAmount = Math.abs(sumBy(getPaymentLineItems(purchase), "amount"));
    const shouldDisplayWarning = isStoreCreditOnlyRefund && isOnFlexSubscription(seller) && partnerFeeAmount;
    const availableRefundAmount = shouldDisplayWarning ? maxRefundAmount - partnerFeeAmount : maxRefundAmount;

    return (
        <Alert className="mb-4" data-testid="split-pay-refund-modal-maximum-refunded-message">
            A maximum of
            <var data-var="availableRefundAmount">
                <strong className="mx-1">
                    <Currency>{availableRefundAmount}</Currency>
                </strong>
            </var>
            can be refunded on this purchase.
            {shouldDisplayWarning && (
                <>
                    {" "}
                    The Service Fee of
                    <var data-var="partnerFeeAmount">
                        <strong className="mx-1">
                            <Currency>{partnerFeeAmount}</Currency>
                        </strong>
                    </var>
                    cannot be refunded to Store Credit. The guest will not be double charged the Service Fee when using
                    the store credit.
                </>
            )}
        </Alert>
    );
};

const hasAnyInteracRefund = (values: FormValues) => {
    for (const [, paymentFormItems] of Object.entries(values.paymentFormItems)) {
        for (const paymentFormItem of paymentFormItems) {
            const isInteracCardRefund =
                paymentFormItem.paymentMethodType === PaymentMethodType.INTERAC &&
                paymentFormItem.refundMethod === PaymentMethod.CREDIT_CARD;
            if (isInteracCardRefund && paymentFormItem.refundTerm !== RefundTerms.NONE) {
                return true;
            }
        }
    }

    return false;
};
